/*
 * ===============================================================================================================
 *                                Copyright 2023, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from '@mui/material';
import { MRT_Row, MRT_RowData } from 'material-react-table';

import FormDialog from '../../../shared/Dialog/FormDialog';
import CustomTablePagination from '../../../shared/CustomTablePagination';
import Loader from '../../../shared/Loader';
import { getEntityPolicyRule } from '../../../shared/PolicyDialog/PolicyUtils';
import {
  fetchBuiltinPoliciesToAssociate,
  fetchGlobalRoleBuiltinPolicies
} from '../../../store/role/GlobalRole/globalRole';
import { clearBuiltinPoliciesToAssociate } from '../../../store/role/GlobalRole/globalRoleActionCreator';
import { hideSnackbar, setSnackBarMessage } from '../../../store/snackBar/snackBarActionCreator';
import {
  selectBuiltinPoliciesToAssociate,
  selectBuiltinPoliciesToAssociateCount,
  selectBuiltinPoliciesByRoleId
} from '../../../store/role/GlobalRole/globalRoleSelector';
import { selectAppLocation } from '../../../store/appLocation/appLocationSelector';
import { associateBuiltinPoliciesToGlobalRole } from '../../../services/policy';
import {
  CANCEL_BTN_TEXT,
  SAVE_BTN_TEXT,
  POLICIES_ASSOCIATE_SUCCESS,
  POLICIES_ASSOCIATE_ERROR,
  SNACKBAR_ALERT_DEFAULT_PROPS
} from './constants';
import { ADD_BUILTIN_POLICIES, LABEL, ROWS_PER_PAGE_LABEL } from '../constants';
import { IRole } from '../../../models/Roles/RolesContracts';
import { IBuiltInPolicyItem } from '../../../models/Policies/BuiltInPolicies';

interface AddBuiltinPoliciesDialogProps {
  onClose: () => void;
  selectedItem: IRole;
}

const AddBuiltinPoliciesDialog = ({ onClose, selectedItem }: AddBuiltinPoliciesDialogProps) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [showDialogLoader, setShowDialogLoader] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    fetchBuiltinPoliciesToAssociate(dispatch, {
      'module-id': selectedItem.moduleId ?? 2002000000,
      'page-number': pageNumber,
      'page-size': pageSize
    });
  }, [dispatch, selectedItem.moduleId, pageNumber, pageSize]);

  const builtinPoliciesToAssociate: IBuiltInPolicyItem[] = useSelector(selectBuiltinPoliciesToAssociate);
  const builtinPoliciesToAssociateCount = useSelector(selectBuiltinPoliciesToAssociateCount);
  const associatedPolicies: IBuiltInPolicyItem[] = useSelector((state: any) =>
    selectBuiltinPoliciesByRoleId(state, selectedItem.id)
  );
  const appLocation = useSelector(selectAppLocation);

  const data = useMemo(
    () =>
      builtinPoliciesToAssociate.map(policy => ({
        ...policy,
        id: `${policy.id}`
      })),
    [builtinPoliciesToAssociate]
  );

  const associatedPoliciesSet = useMemo(() => new Set(associatedPolicies.map(policy => `${policy.id}`)), [
    associatedPolicies
  ]);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const columns = [
    {
      header: LABEL.NAME,
      accessorKey: 'displayName'
    },
    {
      header: LABEL.DESCRIPTION,
      accessorKey: 'description'
    },
    {
      header: LABEL.RULE,
      accessorKey: 'expression',
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => getEntityPolicyRule(JSON.parse(row.original.expression))
    }
  ];

  const displaySnackbar = (message: string, severity: 'success' | 'error') => {
    dispatch(
      setSnackBarMessage({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        message: message,
        severity: severity
      })
    );
  };

  const snackbarSetHidden = () => {
    dispatch(hideSnackbar());
  };

  const handleClose = () => {
    onClose();
    dispatch(clearBuiltinPoliciesToAssociate());
  };

  const handleSave = async () => {
    setShowDialogLoader(true);
    snackbarSetHidden();
    try {
      await associateBuiltinPoliciesToGlobalRole(selectedItem.id, selectedIds.map(id => Number(id)));
      fetchGlobalRoleBuiltinPolicies(dispatch, selectedItem, appLocation?.name);
      handleClose();
      displaySnackbar(POLICIES_ASSOCIATE_SUCCESS + selectedItem.name, 'success');
    } catch (error: any) {
      displaySnackbar(POLICIES_ASSOCIATE_ERROR + error, 'error');
      setShowDialogLoader(false);
    }
  };

  return (
    <FormDialog
      openModal={true}
      handleClose={handleClose}
      dialogTitle={ADD_BUILTIN_POLICIES}
      dialogLoader={<Loader showLoader={showDialogLoader} />}
      dialogAction1={
        <Button onClick={handleClose} variant="outlined" color="primary">
          {CANCEL_BTN_TEXT}
        </Button>
      }
      dialogAction2={
        <Button onClick={handleSave} disabled={selectedIds.length === 0} variant="contained" color="primary">
          {SAVE_BTN_TEXT}
        </Button>
      }
    >
      <CustomTablePagination
        data={data}
        columns={columns}
        loading={builtinPoliciesToAssociateCount === undefined}
        enableRowSelection={row => !associatedPoliciesSet.has(row.original.id)}
        selectedIds={selectedIds}
        onSelectedIdsChange={setSelectedIds}
        currentPageNumber={pageNumber}
        currentItemsPerPage={pageSize}
        rowsPerPageOptions={[1, 5, 10]}
        totalCount={builtinPoliciesToAssociateCount}
        withoutPagination={data.length === 0}
        onChangePageOptions={(itemsPerPage: number, page: number) => {
          setPageSize(itemsPerPage);
          setPageNumber(page);
        }}
        labelRowsPerPage={ROWS_PER_PAGE_LABEL}
      />
    </FormDialog>
  );
};

export default AddBuiltinPoliciesDialog;
